import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  Chip,
  Avatar,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  TablePagination,
  TableSortLabel,
  CircularProgress,
  Alert,
  Snackbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Tooltip,
  Checkbox,
  Menu,
  ImageList,
  ImageListItem,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { useNavigate } from "react-router-dom";
import { Product } from "../types/Product";
import * as api from "../services/api";
import { config } from "../config/environment";
import { log, handleApiError, getPageSize } from "../utils/helpers";

interface Category {
  id: number;
  name: string;
}

type Order = "asc" | "desc";

interface HeadCell {
  id: keyof Product | "category";
  label: string;
  sortable: boolean;
}

const headCells: HeadCell[] = [
  { id: "image", label: "Image", sortable: false },
  { id: "name", label: "Name", sortable: true },
  { id: "description", label: "Description", sortable: true },
  { id: "category", label: "Category", sortable: true },
  { id: "specifications", label: "Specifications", sortable: false },
  { id: "advantages", label: "Advantages", sortable: false },
];

const ProductList: React.FC = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(getPageSize());
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof Product | "category">("name");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    productId: number | null;
    productName: string;
  }>({
    open: false,
    productId: null,
    productName: "",
  });
  const [refreshing, setRefreshing] = useState(false);
  const [selected, setSelected] = useState<number[]>([]);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [exportMenu, setExportMenu] = useState<null | HTMLElement>(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const [productsData, categoriesData] = await Promise.all([
        api.getProducts(),
        fetch(`${config.api.baseUrl}/categories`).then((res) => res.json()),
      ]);

      setProducts(productsData);
      setCategories(categoriesData);
      log("Data fetched successfully", "debug", {
        productsCount: productsData.length,
        categoriesCount: categoriesData.length,
      });
    } catch (error) {
      setError(error instanceof Error ? error.message : "An error occurred");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("Are you sure you want to delete this product?")) {
      try {
        await api.deleteProduct(id);
        setProducts(products.filter((product) => product.id !== id));
        setSnackbar({
          open: true,
          message: "Product deleted successfully",
          severity: "success",
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message:
            error instanceof Error ? error.message : "Failed to delete product",
          severity: "error",
        });
      }
    }
  };

  const handleDeleteClick = (product: Product) => {
    setDeleteDialog({
      open: true,
      productId: product.id,
      productName: product.name,
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.productId) return;

    try {
      await api.deleteProduct(deleteDialog.productId);
      setProducts(
        products.filter((product) => product.id !== deleteDialog.productId)
      );
      setSnackbar({
        open: true,
        message: "Product deleted successfully",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : "Failed to delete product",
        severity: "error",
      });
    } finally {
      setDeleteDialog({
        open: false,
        productId: null,
        productName: "",
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({
      open: false,
      productId: null,
      productName: "",
    });
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: keyof Product | "category") => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const compareValues = (
    a: any,
    b: any,
    orderBy: keyof Product | "category"
  ) => {
    if (orderBy === "category") {
      const categoryA = a.category?.name || "";
      const categoryB = b.category?.name || "";
      return categoryA.localeCompare(categoryB);
    }

    if (b[orderBy] < a[orderBy]) {
      return 1;
    }
    if (b[orderBy] > a[orderBy]) {
      return -1;
    }
    return 0;
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchData();
      setSnackbar({
        open: true,
        message: "Data refreshed successfully",
        severity: "success",
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : "Failed to refresh data",
        severity: "error",
      });
    } finally {
      setRefreshing(false);
    }
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch =
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.description.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCategory =
      selectedCategory === "all" ||
      product.categoryId.toString() === selectedCategory;

    return matchesSearch && matchesCategory;
  });

  const sortedProducts = filteredProducts.sort((a, b) => {
    const result = compareValues(a, b, orderBy);
    return order === "desc" ? -result : result;
  });

  const paginatedProducts = sortedProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      const newSelected = paginatedProducts.map((product) => product.id);
      setSelected(newSelected);
      return;
    }
    setSelected([]);
  };

  const handleSelectClick = (id: number) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: number[] = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }

    setSelected(newSelected);
  };

  const handleBulkDelete = () => {
    setBulkDeleteDialog(true);
  };

  const handleBulkDeleteConfirm = async () => {
    try {
      await Promise.all(selected.map((id) => api.deleteProduct(id)));

      setProducts(products.filter((product) => !selected.includes(product.id)));
      setSnackbar({
        open: true,
        message: `Successfully deleted ${selected.length} products`,
        severity: "success",
      });
      setSelected([]);
    } catch (error) {
      setSnackbar({
        open: true,
        message:
          error instanceof Error ? error.message : "Failed to delete products",
        severity: "error",
      });
    } finally {
      setBulkDeleteDialog(false);
    }
  };

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  const handleExportClick = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenu(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenu(null);
  };

  const exportToCSV = () => {
    const headers = [
      "ID",
      "Name",
      "Description",
      "Category",
      "Specifications",
      "Advantages",
      "Image",
    ];

    const data = products.map((product) => [
      product.id,
      product.name,
      product.description,
      product.category?.name || "",
      parseJSON<{ key: string; value: string }[]>(product.specifications, [])
        .map((spec) => `${spec.key}: ${spec.value}`)
        .join("; "),
      parseJSON<string[]>(product.advantages, []).join("; "),
      product.image,
    ]);

    const csvContent =
      headers.join(",") +
      "\n" +
      data.map((row) => row.map((cell) => `"${cell}"`).join(",")).join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "products.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToJSON = () => {
    const jsonData = products.map((product) => ({
      ...product,
      specifications: parseJSON<{ key: string; value: string }[]>(
        product.specifications,
        []
      ),
      advantages: parseJSON<string[]>(product.advantages, []),
    }));

    const blob = new Blob([JSON.stringify(jsonData, null, 2)], {
      type: "application/json",
    });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.setAttribute("download", "products.json");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Helper function to safely parse JSON
  const parseJSON = <T,>(jsonString: string, fallback: T): T => {
    try {
      return JSON.parse(jsonString);
    } catch (e) {
      console.error("Error parsing JSON:", e);
      return fallback;
    }
  };

  // Function to render specifications
  const renderSpecifications = (specifications: string) => {
    const specs = parseJSON<{ key: string; value: string }[]>(
      specifications,
      []
    );
    return (
      <div style={{ maxWidth: 300 }}>
        {specs.map((spec, index) => (
          <div key={index}>
            <strong>{spec.key}:</strong> {spec.value}
          </div>
        ))}
      </div>
    );
  };

  // Function to render advantages
  const renderAdvantages = (advantages: string) => {
    const advList = parseJSON<string[]>(advantages, []);
    return (
      <div>
        {advList.map((advantage, index) => (
          <div key={index}>{advantage}</div>
        ))}
      </div>
    );
  };

  const renderProductImages = (product: Product) => {
    const images = product.images || [];
    const mainImage = product.image;

    // If there are no additional images, just show the main image
    if (images.length === 0) {
      return (
        <Avatar
          src={api.getImageUrl(mainImage)}
          alt={product.name}
          variant="rounded"
          sx={{ width: 100, height: 100 }}
        />
      );
    }

    // Show image grid if there are multiple images
    return (
      <ImageList sx={{ width: 200, height: 120 }} cols={2} rowHeight={60}>
        {images.map((image, index) => (
          <ImageListItem key={index}>
            <img
              src={api.getImageUrl(image.url)}
              alt={`${product.name} ${index + 1}`}
              loading="lazy"
              style={{ objectFit: "cover", height: "100%" }}
            />
          </ImageListItem>
        ))}
      </ImageList>
    );
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ mt: 3 }}>
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 3 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h4">Products</Typography>
          <Tooltip title="Refresh data">
            <IconButton
              onClick={handleRefresh}
              disabled={refreshing}
              sx={{ ml: 2 }}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleExportClick}
          >
            Export
          </Button>
          {selected.length > 0 && (
            <Button
              variant="contained"
              color="error"
              onClick={handleBulkDelete}
            >
              Delete Selected ({selected.length})
            </Button>
          )}
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate("/products/add")}
          >
            Add Product
          </Button>
        </Stack>
      </Box>

      <Menu
        anchorEl={exportMenu}
        open={Boolean(exportMenu)}
        onClose={handleExportClose}
      >
        <MenuItem onClick={exportToCSV}>Export as CSV</MenuItem>
        <MenuItem onClick={exportToJSON}>Export as JSON</MenuItem>
      </Menu>

      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <TextField
          label="Search"
          variant="outlined"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          sx={{ flexGrow: 1 }}
        />
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel id="category-filter-label">Category</InputLabel>
          <Select
            labelId="category-filter-label"
            value={selectedCategory}
            label="Category"
            onChange={(e) => setSelectedCategory(e.target.value)}
          >
            <MenuItem value="all">All Categories</MenuItem>
            {categories.map((category) => (
              <MenuItem key={category.id} value={category.id.toString()}>
                {category.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selected.length > 0 &&
                      selected.length < paginatedProducts.length
                    }
                    checked={
                      paginatedProducts.length > 0 &&
                      selected.length === paginatedProducts.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    sortDirection={orderBy === headCell.id ? order : false}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={orderBy === headCell.id}
                        direction={orderBy === headCell.id ? order : "asc"}
                        onClick={() => handleRequestSort(headCell.id)}
                      >
                        {headCell.label}
                      </TableSortLabel>
                    ) : (
                      headCell.label
                    )}
                  </TableCell>
                ))}
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paginatedProducts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <Typography variant="body1" sx={{ py: 2 }}>
                      No products found
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedProducts.map((product) => {
                  const isItemSelected = isSelected(product.id);
                  return (
                    <TableRow key={product.id} selected={isItemSelected} hover>
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isItemSelected}
                          onChange={() => handleSelectClick(product.id)}
                        />
                      </TableCell>
                      <TableCell>{renderProductImages(product)}</TableCell>
                      <TableCell>
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {product.name}
                          {product.isFeatured && (
                            <Chip
                              label="Featured"
                              size="small"
                              color="warning"
                              sx={{ fontWeight: "bold" }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        {product.description.length > 100
                          ? `${product.description.substring(0, 100)}...`
                          : product.description}
                      </TableCell>
                      <TableCell>
                        {product.category && (
                          <Chip label={product.category.name} size="small" />
                        )}
                      </TableCell>
                      <TableCell>
                        {renderSpecifications(product.specifications)}
                      </TableCell>
                      <TableCell>
                        {renderAdvantages(product.advantages)}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1}>
                          <IconButton
                            onClick={() =>
                              navigate(`/products/edit/${product.id}`)
                            }
                            color="primary"
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            onClick={() => handleDeleteClick(product)}
                            color="error"
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredProducts.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>
      )}

      <Dialog open={deleteDialog.open} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Product</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the product "
            {deleteDialog.productName}"? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={bulkDeleteDialog}
        onClose={() => setBulkDeleteDialog(false)}
      >
        <DialogTitle>Delete Products</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete {selected.length} selected products?
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleBulkDeleteConfirm} color="error" autoFocus>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductList;
